import { useState, useEffect, useMemo } from 'react';
import { Plus, LogOut, Search, User, Crown, Loader2, TrendingUp, Bell, LayoutGrid, CalendarDays } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { supabase } from '../lib/supabase';
import { Countdown, CATEGORIES } from '../types/database';
import { CountdownCard } from './CountdownCard';
import { CountdownModal } from './CountdownModal';
import { CountdownDetail } from './CountdownDetail';
import { AccountModal } from './AccountModal';
import { ProUpgradeModal } from './ProUpgradeModal';
import ConfirmModal from './ConfirmModal';
import { CalendarView } from './CalendarView';
import { processRecurringCountdowns } from '../utils/recurringCountdowns';
import { NotificationManager } from '../utils/notifications';

interface DashboardProps {
  openCreateModal?: boolean;
  onModalStateChange?: (isOpen: boolean) => void;
}

export function Dashboard({ openCreateModal = false, onModalStateChange }: DashboardProps) {
  const { user, signOut } = useAuth();
  const { isPro, loading: subLoading } = useSubscription();
  const [countdowns, setCountdowns] = useState<Countdown[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editingCountdown, setEditingCountdown] = useState<Countdown | null>(null);
  const [detailCountdown, setDetailCountdown] = useState<Countdown | null>(null);
  const [deleteRequest, setDeleteRequest] = useState<{ open: boolean; event: Countdown | null }>({ open: false, event: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [viewMode, setViewMode] = useState<'grid' | 'calendar'>('grid');
  const [notifPermission, setNotifPermission] = useState('default');

  useEffect(() => {
    loadCountdowns();
    if ('Notification' in window) {
      setNotifPermission(Notification.permission);
    }
  }, []);

  const loadCountdowns = async () => {
    try {
      setError(null);
      const { data, error } = await supabase
        .from('countdowns')
        .select('*')
        .order('event_date', { ascending: true });

      if (error) throw error;

      const countdownsData = data || [];
      await processRecurringCountdowns(countdownsData);

      const { data: updatedData } = await supabase
        .from('countdowns')
        .select('*')
        .order('event_date', { ascending: true });

      const finalData = updatedData || [];
      setCountdowns(finalData);
      NotificationManager.checkUpcomingEvents(finalData);
    } catch (error: any) {
      console.error('Error loading countdowns:', error);
      setError(error.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRequest.event) return;
    const id = deleteRequest.event.id;
    try {
      const { error } = await supabase.from('countdowns').delete().eq('id', id);
      if (error) throw error;
      setCountdowns(countdowns.filter(c => c.id !== id));
      setDeleteRequest({ open: false, event: null });
    } catch (error) {
      console.error('Error deleting countdown:', error);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingCountdown(null);
    if (onModalStateChange) onModalStateChange(false);
    loadCountdowns();
  };

  const { filteredCountdowns, upcomingCountdowns, pastCountdowns } = useMemo(() => {
    if (!Array.isArray(countdowns)) return { filteredCountdowns: [], upcomingCountdowns: [], pastCountdowns: [] };
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const filtered = countdowns.filter(c => {
      if (!c || !c.name) return false;
      const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || c.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    const upcoming = filtered
      .filter(c => new Date(c.event_date) >= now)
      .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());

    const past = filtered
      .filter(c => new Date(c.event_date) < now)
      .sort((a, b) => b.created_at.localeCompare(a.created_at));

    return { filteredCountdowns: filtered, upcomingCountdowns: upcoming, pastCountdowns: past };
  }, [countdowns, searchQuery, selectedCategory]);

  const daysToNext = upcomingCountdowns[0] 
    ? Math.ceil((new Date(upcomingCountdowns[0].event_date).getTime() - new Date().setHours(0,0,0,0)) / (1000 * 60 * 60 * 24)) 
    : 0;

  if (loading || subLoading) {
    return (
      <div className="min-h-screen bg-[#feeddd] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#9e142f] animate-spin" />
      </div>
    );
  }

  // Check 1: Early Return for Countdown Detail View
  if (detailCountdown) {
    return (
      <CountdownDetail
        countdown={detailCountdown}
        onClose={() => setDetailCountdown(null)}
        onEdit={(countdown) => {
          setDetailCountdown(null);
          setEditingCountdown(countdown);
          setShowModal(true);
        }}
        onDelete={() => {
          setDeleteRequest({ open: true, event: detailCountdown });
          setDetailCountdown(null);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#feeddd] font-inter text-[#1a3e54]">
      <nav className="px-6 py-8 md:px-12 flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <span className="text-3xl">⏳</span>
          <h1 className="text-3xl font-playfair font-bold tracking-tight">Countr</h1>
          {isPro && (
            <div className="bg-yellow-400 p-1.5 rounded-lg shadow-sm ml-2" title="Pro Member">
              <Crown className="w-4 h-4 text-white fill-white" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-[#1a3e54]/5 rounded-xl p-1 mr-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-white text-[#1a3e54] shadow-sm' : 'text-[#1a3e54]/40 hover:text-[#1a3e54]'}`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'calendar' ? 'bg-white text-[#1a3e54] shadow-sm' : 'text-[#1a3e54]/40 hover:text-[#1a3e54]'}`}
              title="Calendar view"
            >
              <CalendarDays className="w-4 h-4" />
            </button>
          </div>
          {notifPermission === 'default' && (
            <button 
              onClick={() => NotificationManager.requestPermission().then(granted => setNotifPermission(granted ? 'granted' : 'denied'))}
              className="p-3 bg-white hover:bg-white/80 rounded-2xl text-[#9e142f] shadow-sm transition-all flex items-center gap-2"
            >
              <Bell className="w-5 h-5" />
              <span className="hidden lg:inline text-xs font-bold uppercase tracking-wider">Enable Alerts</span>
            </button>
          )}
          <button onClick={() => setShowAccountModal(true)} className="p-3 bg-white hover:bg-white/80 rounded-2xl text-[#1a3e54] shadow-sm transition-all">
            <User className="w-5 h-5" />
          </button>
          <button onClick={signOut} className="p-3 bg-white hover:bg-white/80 rounded-2xl text-[#1a3e54] shadow-sm transition-all">
            <LogOut className="w-5 h-5" />
          </button>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-6 py-3 bg-[#9e142f] hover:bg-[#801026] text-white rounded-2xl text-sm font-bold shadow-lg hover:shadow-xl transition-all ml-2">
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">New Event</span>
          </button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12 animate-fadeIn">
          <div className="bg-[#1a3e54] rounded-[2rem] p-8 text-[#feeddd] shadow-xl relative overflow-hidden group">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60 mb-2">Total Events</p>
            <div className="flex items-end gap-3">
              <span className="text-4xl font-black leading-none">{countdowns.length}</span>
              <TrendingUp className="w-6 h-6 mb-1 text-[#9e142f]" />
            </div>
          </div>
          <div className="bg-white rounded-[2rem] p-8 shadow-sm border-2 border-[#1a3e54]/5">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#1a3e54]/40 mb-2">Next Event In</p>
            <div className="flex items-end gap-3">
              <span className="text-4xl font-black text-[#1a3e54] leading-none">{daysToNext}</span>
              <span className="text-sm font-bold text-[#1a3e54]/60 mb-1">Days</span>
            </div>
          </div>
          <div className="bg-[#9e142f] rounded-[2rem] p-8 text-white shadow-xl">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60 mb-2">Moments</p>
            <div className="flex items-end gap-3">
              <span className="text-4xl font-black leading-none">{upcomingCountdowns.length}</span>
              <span className="text-sm font-bold opacity-80 mb-1">Upcoming</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-10 overflow-visible">
          <div className="relative flex-shrink-0 w-full max-w-[180px] group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1a3e54]/30" />
            <input 
              type="text" 
              placeholder="Search..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-[6px] bg-white border-2 border-transparent rounded-xl focus:border-[#9e142f] outline-none shadow-sm transition-all text-[13px] font-medium" 
            />
          </div>
          <div className="flex-1 overflow-x-auto">
            <div className="flex gap-2">
              {['All', ...CATEGORIES].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-xl text-[11px] font-bold uppercase tracking-wider whitespace-nowrap transition-all ${
                    selectedCategory === cat 
                      ? 'bg-[#1a3e54] text-[#feeddd]' 
                      : 'bg-white text-[#1a3e54]/40 hover:text-[#1a3e54]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {viewMode === 'calendar' ? (
          <div className="animate-fadeIn">
            <CalendarView 
              countdowns={filteredCountdowns} 
              onCountdownClick={(c) => setDetailCountdown(c)} 
            />
          </div>
        ) : (
          <div className="space-y-16">
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingCountdowns.map((countdown, index) => (
                <CountdownCard 
                  key={countdown.id} 
                  countdown={countdown} 
                  index={index}
                  onShowDetail={() => setDetailCountdown(countdown)}
                  onEdit={(c) => { setEditingCountdown(c); setShowModal(true); }}
                  onDeleteRequest={(c) => setDeleteRequest({ open: true, event: c })}
                />
              ))}
            </section>

            {pastCountdowns.length > 0 && (
              <section className="animate-fadeIn">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-px flex-1 bg-[#1a3e54]/10"></div>
                  <h2 className="text-xs font-black uppercase tracking-[0.3em] text-[#1a3e54]/40">Past Events</h2>
                  <div className="h-px flex-1 bg-[#1a3e54]/10"></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pastCountdowns.map((countdown, index) => (
                    <CountdownCard 
                      key={countdown.id} 
                      countdown={countdown} 
                      index={index}
                      onShowDetail={() => setDetailCountdown(countdown)}
                      onEdit={(c) => { setEditingCountdown(c); setShowModal(true); }}
                      onDeleteRequest={(c) => setDeleteRequest({ open: true, event: c })}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      {showModal && <CountdownModal countdown={editingCountdown} onClose={handleModalClose} />}
      {showAccountModal && <AccountModal onClose={() => setShowAccountModal(false)} onUpgrade={() => setShowUpgradeModal(true)} />}
      {showUpgradeModal && <ProUpgradeModal onClose={() => setShowUpgradeModal(false)} />}
      
      <ConfirmModal
        isOpen={deleteRequest.open}
        title="Delete Moment?"
        message={`Are you sure you want to delete "${deleteRequest.event?.name}"? You won't be able to recover this countdown.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteRequest({ open: false, event: null })}
      />
    </div>
  );
}